import type { Point2D, Point3D } from "./types";

/**
 * Small geometry helpers shared by two-point draggers (Rectangle/Ellipse/Line
 * in sequential mode), mirroring the bounds/distance math OneWayDragger uses
 * for its single-stroke equivalent.
 */

export function pointDistance(a: Point2D, b: Point2D): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/** Rejects near-zero-size shapes from an accidental double-click. */
export function hasMinimumSeparation(
  a: Point2D,
  b: Point2D,
  minPixels = 5,
): boolean {
  return pointDistance(a, b) >= minPixels;
}

export interface PointBounds {
  screenMin: Point2D;
  screenMax: Point2D;
  worldMin: Point3D;
  worldMax: Point3D;
  width: number;
  height: number;
  worldWidth: number;
  worldHeight: number;
}

/** Normalized min/max bounds (screen + world) from two points, regardless of placement order. */
export function getBoundsFromPoints(
  aScreen: Point2D,
  bScreen: Point2D,
  aWorld: Point3D,
  bWorld: Point3D,
): PointBounds {
  const screenMin: Point2D = {
    x: Math.min(aScreen.x, bScreen.x),
    y: Math.min(aScreen.y, bScreen.y),
  };
  const screenMax: Point2D = {
    x: Math.max(aScreen.x, bScreen.x),
    y: Math.max(aScreen.y, bScreen.y),
  };
  const worldMin: Point3D = {
    x: Math.min(aWorld.x, bWorld.x),
    y: Math.min(aWorld.y, bWorld.y),
    z: 0,
  };
  const worldMax: Point3D = {
    x: Math.max(aWorld.x, bWorld.x),
    y: Math.max(aWorld.y, bWorld.y),
    z: 0,
  };

  return {
    screenMin,
    screenMax,
    worldMin,
    worldMax,
    width: screenMax.x - screenMin.x,
    height: screenMax.y - screenMin.y,
    worldWidth: worldMax.x - worldMin.x,
    worldHeight: worldMax.y - worldMin.y,
  };
}
